import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from 'common/components/Icon';
import { ICONS } from 'icons/iconsMap';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import { Text } from 'common/styledComponents/typography';
import { getErrorText } from 'modules/settings/components/DataExport/utils';
import {
  EmptyContainer,
  ErrorContainer,
  SkeletonBar,
  SkeletonRow,
  SkeletonRows,
  StateIcon,
} from 'modules/settings/components/DataExport/styled';

const SKELETON_WIDTHS = [
  ['70%', '40%', '80%', '60%'],
  ['55%', '40%', '75%', '50%'],
  ['65%', '40%', '70%', '55%'],
];

/** Placeholder rows: the card keeps its title and description while its list loads. */
export const SectionLoading: React.FC = () => (
  <SkeletonRows data-testid='data-export-loading' aria-busy='true'>
    {SKELETON_WIDTHS.map((widths, row) => (
      <SkeletonRow key={row}>
        {widths.map((width, column) => (
          <SkeletonBar key={column} width={width} />
        ))}
      </SkeletonRow>
    ))}
  </SkeletonRows>
);

export const SectionError: React.FC<{ error: unknown; onRetry: () => void }> = ({ error, onRetry }) => {
  const { t } = useTranslation();

  return (
    <ErrorContainer role='alert'>
      <Icon icon='alert-fill' size={24} color='red700' />
      <div style={{ flex: 1 }}>
        <Text variant='body2' color='black'>
          {getErrorText(error, t('dataExport.loadError'))}
        </Text>
      </div>
      <SecondaryButton type='button' color='white' isButtonV2 onClick={onRetry}>
        {t('dataExport.retry')}
      </SecondaryButton>
    </ErrorContainer>
  );
};

interface EmptyProps {
  icon: keyof typeof ICONS;
  /** What is missing, e.g. "No tokens yet". */
  title: string;
  message: string;
  /** The same action as the section's header button, offered again where the rows would be. */
  action?: { label: string; onClick: () => void; disabled?: boolean };
}

export const SectionEmpty: React.FC<EmptyProps> = ({ icon, title, message, action }) => (
  <EmptyContainer data-testid='data-export-empty'>
    <StateIcon>
      <Icon icon={icon} size={24} color='latana' />
    </StateIcon>
    <Text variant='h6' fontWeight='700' color='black'>
      {title}
    </Text>
    <Text variant='body2' color='black50'>
      {message}
    </Text>
    {action && (
      <SecondaryButton type='button' color='white' isButtonV2 disabled={action.disabled} onClick={action.onClick} style={{ marginTop: 12 }}>
        {action.label}
      </SecondaryButton>
    )}
  </EmptyContainer>
);
